/** Same-origin kernel calls through the session gateway (it injects the kernel token). */
import { KERNEL_API_URL } from "./config";

export async function kernel<T>(path: string, body?: unknown, method?: "PUT" | "DELETE"): Promise<T> {
  const res = await fetch(`${KERNEL_API_URL}${path}`, {
    method: method ?? (body === undefined ? "GET" : "POST"),
    credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((data as { error?: string }).error ?? `HTTP ${res.status}`), { status: res.status, data });
  return data as T;
}

// ── pack-decisions ────────────────────────────────────────────────────
export interface Finding {
  table: string;
  obligation: string;
  subject: { type: string; id: string };
  status: "met" | "unmet";
  requirements: string[];
  severity: "blocking" | "warning" | "advice";
  deadlineHours?: number;
  reasons: string[];
  trace: { rows: string[]; facts: Record<string, unknown> };
  draft: boolean;
}
export interface TableInfo { table: string; title?: string; subject: string; stage: string; draft: boolean }
export interface Evaluation { findings: Finding[]; unmet: number; graph: { nodes: number; edges: number } }

// ── pack-journeys ─────────────────────────────────────────────────────
export interface JourneyInfo { journey: string; title: string; frameworks: string[]; reuses: string[]; phases: string[] }
export interface Prompt {
  phase: string; step: string; key: string; ask: string; mode: string;
  item?: { id: string; type: string; label: string };
  known?: unknown; knownFrom?: "base" | "draft";
  options?: Array<Record<string, unknown>>; previous?: unknown; answered: boolean;
  // pack-journeys 0.4.0
  candidates?: Array<{ id: string; label: string; parent?: string; exists?: boolean }>;
  rows?: Array<{ id: string; type: string; label: string; known?: unknown; allowed: string[] | null }>;
  suggested?: unknown;
  copyFrom?: Array<{ id: string; label: string }>;
}
export interface PhaseProgress { phase: string; title: string; prompts: number; answered: number; gate?: { passed: boolean; met: number; unmet: number; blocking: number } }
export interface SegmentPreview { matching: number; alreadyCovered: number; sample: Array<{ id: string; label: string }> }
export interface Condition { path: string; cell: string | boolean | number }
/** pack-journeys 0.5: a half-done session saved per user (resume from any browser). */
export interface SavedDraft { session: Session | null; savedAt?: string }
export type Session = { journey: string; answered: string[]; draft: { created: Array<{ id: string; type: string; fields: Record<string, unknown> }>; edges: unknown[] }; [k: string]: unknown };
export interface Gate { phase: string; tables: string[]; passed: boolean; blocking: Finding[]; findings: Finding[] }

/** `scope`: an organisation-scope query fragment (`&filter=…`, ADR-037), or empty. */
export const entityCount = (type: string, scope = "") =>
  kernel<{ total: number }>(`/entities?type=${encodeURIComponent(type)}${scope}&limit=1`).then((r) => r.total ?? 0).catch(() => 0);
